import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  Grid,
  useMediaQuery,
  useTheme,
  alpha,
  Paper,
  Popover,
} from "@mui/material";
import {
  Add,
  Search,
  Refresh,
  ErrorOutline,
  Edit,
  Close,
  Visibility,
  Print,
  Download,
  Delete,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAllBOMs, deleteBOM } from "../../../Services/Services";
import { generateBOMPdf } from "./BOMPdf";

const BOMList = () => {
  const [boms, setBoms] = useState([]);
  const [filteredBoms, setFilteredBoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedBom, setSelectedBom] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [deleteBom, setDeleteBom] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const primaryBlue = theme.palette.primary.main;
  const lightBlue = alpha(theme.palette.primary.main, 0.08);
  const mediumBlue = alpha(theme.palette.primary.main, 0.2);

  const tableHeadBg = isDark
    ? alpha(theme.palette.primary.main, 0.15)
    : "#f0f4ff";

  const rowBg = (index) =>
    index % 2 === 0
      ? isDark
        ? alpha(theme.palette.common.white, 0.035)
        : "#ffffff"
      : isDark
        ? alpha(theme.palette.common.white, 0.06)
        : "#f5f5f5";

  useEffect(() => {
    fetchBOMs();
  }, []);

  useEffect(() => {
    filterBOMs();
  }, [searchTerm, boms]);

  const fetchBOMs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllBOMs();
      const data = response.results || response || [];
      setBoms(data);
      setFilteredBoms(data);
    } catch (err) {
      setError("Failed to load BOMs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filterBOMs = () => {
    if (!searchTerm.trim()) {
      setFilteredBoms(boms);
      return;
    }
    const term = searchTerm.toLowerCase().trim();
    setFilteredBoms(
      boms.filter(
        (bom) =>
          bom.bom_no.toLowerCase().includes(term) ||
          (bom.bom_name && bom.bom_name.toLowerCase().includes(term)) ||
          bom.bom_date.includes(term),
      ),
    );
  };

  const handleViewClick = (bom) => {
    setSelectedBom(bom);
    setViewModalOpen(true);
  };

  const handleCloseView = () => {
    setViewModalOpen(false);
    setSelectedBom(null);
  };

  // ✅ SIMPLE - Navigate to BOM form with ID
  const handleEditClick = (bom) => {
    navigate(`/dashboard/BOMcreation/${bom.id}`);
  };

  const handlePrintPDF = async () => {
    if (!selectedBom) return;
    setPrintLoading(true);
    try {
      await generateBOMPdf(selectedBom, "open");
      toast.success("PDF opened in new tab!");
    } catch (err) {
      toast.error("Failed to generate PDF.");
    } finally {
      setPrintLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedBom) return;
    setPrintLoading(true);
    try {
      await generateBOMPdf(selectedBom, "download");
      toast.success("PDF downloaded!");
    } catch (err) {
      toast.error("Failed to download PDF.");
    } finally {
      setPrintLoading(false);
    }
  };

  const handleQuickPrint = async (bom) => {
    setPrintLoading(true);
    try {
      await generateBOMPdf(bom, "open");
      toast.success("PDF opened!");
    } catch (err) {
      toast.error("Failed to generate PDF.");
    } finally {
      setPrintLoading(false);
    }
  };

  const getDisplayName = (obj) => {
    if (!obj) return "N/A";
    if (typeof obj === "object") {
      if (obj.name) return obj.name;
      if (obj.size) return obj.size;
    }
    return obj;
  };

  const handleDeleteClick = (event, bom) => {
    setDeleteAnchorEl(event.currentTarget);
    setDeleteBom(bom);
  };

  const handleCloseDelete = () => {
    setDeleteAnchorEl(null);
    setDeleteBom(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteBom) return;
    setDeleting(true);
    try {
      await deleteBOM(deleteBom.id);
      toast.success(`${deleteBom.bom_no} deleted!`);
      handleCloseDelete();
      fetchBOMs();
    } catch (err) {
      toast.error("Failed to delete BOM.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading && boms.length === 0) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          bgcolor: "background.default",
        }}
      >
        <CircularProgress size={40} thickness={4} color="primary" />
        <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
          Loading BOMs...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          p: 2,
          bgcolor: "background.default",
        }}
      >
        <ErrorOutline sx={{ fontSize: 48, color: "error.main" }} />
        <Alert
          severity="error"
          sx={{ maxWidth: 420 }}
          action={
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={fetchBOMs}
              variant="outlined"
              color="primary"
              sx={{ fontSize: "0.7rem", py: 0.3 }}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "calc(100vh - 110px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        p: 0,
        mt: 1,
        bgcolor: "background.default",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      {/* Search + Action Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 1.5,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <TextField
          placeholder="Search by BOM No., Name, or Date..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            minWidth: 180,
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              height: 32,
              bgcolor: "background.paper",
              "& fieldset": { borderColor: "divider" },
              "&:hover fieldset": { borderColor: "primary.main" },
              "&.Mui-focused fieldset": { borderColor: "primary.main" },
            },
            "& .MuiInputBase-input": {
              fontSize: "0.8rem",
              py: 0.5,
              color: "text.primary",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "text.disabled", fontSize: "0.9rem" }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="contained"
          startIcon={<Add sx={{ fontSize: "1rem" }} />}
          onClick={() => navigate("/dashboard/bomcreation")}
          sx={{
            borderRadius: 0,
            px: 2,
            py: 0.6,
            fontSize: "0.8rem",
            fontWeight: 600,
            textTransform: "none",
            color: "#fff",
            bgcolor: "primary.main",
            boxShadow: `0 2px 8px ${mediumBlue}`,
            whiteSpace: "nowrap",
            "&:hover": { bgcolor: "primary.dark" },
          }}
        >
          Create BOM
        </Button>

        <Button
          variant="outlined"
          startIcon={<Refresh sx={{ fontSize: "0.9rem" }} />}
          onClick={fetchBOMs}
          sx={{
            color: "primary.main",
            borderColor: "primary.main",
            borderRadius: 0,
            textTransform: "none",
            fontSize: "0.75rem",
            py: 0.5,
            px: 1.5,
            whiteSpace: "nowrap",
            "&:hover": {
              bgcolor: lightBlue,
              borderColor: "primary.main",
            },
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* BOM Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          backgroundImage: "none",
          borderRadius: 0,
          boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
          flex: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        <Table sx={{ minWidth: 700 }} size="small" stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  color: "text.primary",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  borderBottom: "2px solid",
                  borderColor: "primary.main",
                  py: 0.6,
                  px: 0.8,
                  whiteSpace: "nowrap",
                  backgroundColor: tableHeadBg,
                  letterSpacing: "0.04em",
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                },
              }}
            >
              <TableCell>S. No.</TableCell>
              <TableCell>BOM No.</TableCell>
              <TableCell>BOM Name</TableCell>
              <TableCell>BOM Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredBoms.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  sx={{
                    textAlign: "center",
                    color: "text.secondary",
                    py: 3,
                    fontSize: "0.85rem",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {searchTerm
                    ? "No BOMs match your search"
                    : "No BOMs found. Create your first BOM!"}
                </TableCell>
              </TableRow>
            ) : (
              filteredBoms.map((bom, index) => (
                <TableRow
                  key={bom.id}
                  sx={{
                    bgcolor: rowBg(index),
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      bgcolor: isDark
                        ? alpha(theme.palette.primary.main, 0.1)
                        : "#e3f2fd",
                    },
                    "& td": {
                      color: "text.primary",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      py: 0.4,
                      px: 0.8,
                      fontSize: "0.8rem",
                    },
                  }}
                >
                  <TableCell sx={{ color: "text.secondary", fontWeight: 500 }}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={{ color: "primary.main", fontWeight: 500 }}>
                    {bom.bom_no}
                  </TableCell>
                  <TableCell>{bom.bom_name || "—"}</TableCell>
                  <TableCell>{bom.bom_date}</TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 0.5,
                      }}
                    >
                      {/* View */}
                      <Tooltip title="View Components">
                        <IconButton
                          size="small"
                          onClick={() => handleViewClick(bom)}
                          sx={{
                            color: "success.main",
                            p: 0.3,
                            "&:hover": {
                              color: "success.dark",
                              bgcolor: (t) =>
                                alpha(t.palette.success.main, 0.1),
                            },
                          }}
                        >
                          <Visibility sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>

                      {/* ✅ Edit - Navigate to form */}
                      <Tooltip title="Edit BOM">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(bom)}
                          sx={{
                            color: "primary.main",
                            p: 0.3,
                            "&:hover": {
                              color: "primary.dark",
                              bgcolor: lightBlue,
                            },
                          }}
                        >
                          <Edit sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>

                      {/* Print */}
                      <Tooltip title="Print BOM">
                        <IconButton
                          size="small"
                          onClick={() => handleViewClick(bom)}
                          sx={{
                            color: "error.main",
                            p: 0.3,
                            "&:hover": {
                              color: "error.dark",
                              bgcolor: (t) => alpha(t.palette.error.main, 0.1),
                            },
                          }}
                        >
                          <Print sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete BOM">
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(e, bom)}
                          sx={{
                            color: "error.main",
                            p: 0.3,
                            "&:hover": {
                              color: "error.dark",
                              bgcolor: (t) => alpha(t.palette.error.main, 0.15),
                            },
                          }}
                        >
                          <Delete sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* VIEW MODAL - Same as before, no change */}
      <Dialog
        open={viewModalOpen}
        onClose={handleCloseView}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 8px 40px rgba(0,0,0,0.6)"
              : "0 8px 40px rgba(0,0,0,0.15)",
            maxHeight: "90vh",
            maxWidth: "800px",
            mx: "auto",
          },
        }}
      >
        {selectedBom && (
          <>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                fontFamily: "Arial, sans-serif",
                bgcolor: "background.paper",
                fontSize: isMobile ? "12px" : "14px",
                color: "text.primary",
                p: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  textAlign: "center",
                  fontWeight: "bold",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  py: 1,
                  fontSize: isMobile ? "1rem" : "1.25rem",
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? alpha(t.palette.primary.main, 0.1)
                      : "#fafafa",
                  color: "text.primary",
                }}
              >
                BILL OF MATERIALS
              </Typography>

              <Grid
                container
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  mt: 1,
                }}
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    p: isMobile ? 0.8 : 1,
                    borderRight: { xs: "none", sm: "1px solid" },
                    borderColor: { sm: "divider" },
                    borderBottom: { xs: "1px solid", sm: "none" },
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  {[
                    {
                      label: "BOM No.",
                      value: selectedBom.bom_no,
                      highlight: true,
                    },
                    { label: "BOM Date", value: selectedBom.bom_date },
                    {
                      label: "BOM Name",
                      value: selectedBom.bom_name || "—",
                    },
                  ].map(({ label, value, highlight }) => (
                    <Box key={label} sx={{ display: "flex", gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{
                          fontSize: isMobile ? "0.75rem" : "0.85rem",
                          minWidth: 80,
                          color: "text.primary",
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: isMobile ? "0.75rem" : "0.85rem",
                          color: highlight ? "primary.main" : "text.secondary",
                          fontWeight: highlight ? 500 : "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    p: isMobile ? 0.8 : 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  {[
                    {
                      label: "Created On",
                      value: new Date(
                        selectedBom.created_at || selectedBom.bom_date,
                      ).toLocaleString(),
                    },
                    {
                      label: "Components",
                      value: selectedBom.components?.length || 0,
                    },
                    {
                      label: "Total Qty",
                      value: selectedBom.components?.reduce(
                        (sum, c) => sum + Number(c.quantity || 0),
                        0,
                      ),
                    },
                  ].map(({ label, value }) => (
                    <Box key={label} sx={{ display: "flex", gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{
                          fontSize: isMobile ? "0.75rem" : "0.85rem",
                          minWidth: 100,
                          color: "text.primary",
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: isMobile ? "0.75rem" : "0.85rem",
                          color: "text.secondary",
                        }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>

              <Box
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  overflowX: "auto",
                  mt: 1,
                }}
              >
                <Table
                  size="small"
                  sx={{
                    borderCollapse: "collapse",
                    "& td, & th": {
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      padding: "6px 8px",
                      fontSize: "0.8rem",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: tableHeadBg,
                        "& th": {
                          fontWeight: "bold",
                          fontSize: "0.8rem",
                          color: "text.primary",
                          borderBottom: "2px solid",
                          borderColor: "primary.main",
                        },
                      }}
                    >
                      {[
                        "S.No.",
                        "Category",
                        "Product Type",
                        "Item",
                        "Unit",
                        "Qty",
                      ].map((h, i) => (
                        <TableCell
                          key={h}
                          align={i === 0 || i >= 4 ? "center" : "left"}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedBom.components || []).map((comp, index) => (
                      <TableRow
                        key={comp.id || index}
                        sx={{
                          bgcolor: (t) =>
                            index % 2 === 0
                              ? t.palette.mode === "dark"
                                ? alpha(t.palette.common.white, 0.03)
                                : "#fafafa"
                              : "background.paper",
                          "&:hover": {
                            bgcolor: (t) =>
                              t.palette.mode === "dark"
                                ? alpha(t.palette.primary.main, 0.08)
                                : "#e3f2fd",
                          },
                          "& td": { color: "text.primary" },
                        }}
                      >
                        <TableCell align="center">{index + 1}</TableCell>
                        <TableCell>
                          {getDisplayName(comp.category) || "-"}
                        </TableCell>
                        <TableCell>
                          {getDisplayName(comp.product_type) || "-"}
                        </TableCell>
                        <TableCell>
                          {getDisplayName(comp.item) || "N/A"}
                        </TableCell>
                        <TableCell align="center">
                          {getDisplayName(comp.unit) || "-"}
                        </TableCell>
                        <TableCell align="center">
                          {comp.quantity || 0}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Grid container sx={{ mt: 1 }}>
                <Grid
                  item
                  xs={12}
                  sm={8}
                  sx={{
                    p: isMobile ? 0.8 : 1,
                    borderRight: { xs: "none", sm: "1px solid" },
                    borderColor: { sm: "divider" },
                    borderBottom: { xs: "1px solid", sm: "none" },
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.3,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{
                      fontSize: isMobile ? "0.75rem" : "0.85rem",
                      color: "text.primary",
                    }}
                  >
                    Summary
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: isMobile ? "0.7rem" : "0.8rem",
                      color: "text.secondary",
                    }}
                  >
                    <strong>Total Components:</strong>{" "}
                    {selectedBom.components?.length || 0}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: isMobile ? "0.7rem" : "0.8rem",
                      color: "text.secondary",
                    }}
                  >
                    <strong>Total Quantity:</strong>{" "}
                    {selectedBom.components?.reduce(
                      (sum, c) => sum + Number(c.quantity || 0),
                      0,
                    )}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      p: isMobile ? 0.5 : 0.6,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.8rem", color: "text.secondary" }}
                    >
                      Components
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.8rem", color: "text.primary" }}
                    >
                      {selectedBom.components?.length || 0}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      p: isMobile ? 0.5 : 0.6,
                      bgcolor: (t) =>
                        t.palette.mode === "dark"
                          ? alpha(t.palette.primary.main, 0.15)
                          : "#f0f0f0",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{ fontSize: "0.9rem", color: "text.primary" }}
                    >
                      Total Qty
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{ fontSize: "0.9rem", color: "text.primary" }}
                    >
                      {selectedBom.components?.reduce(
                        (sum, c) => sum + Number(c.quantity || 0),
                        0,
                      )}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: (t) =>
                  t.palette.mode === "dark"
                    ? alpha(t.palette.common.white, 0.04)
                    : "#fafafa",
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <Button
                onClick={handleCloseView}
                variant="outlined"
                sx={{
                  borderRadius: 0,
                  textTransform: "none",
                  color: "text.secondary",
                  borderColor: "divider",
                  "&:hover": {
                    borderColor: "primary.main",
                    color: "primary.main",
                  },
                }}
              >
                Close
              </Button>
              <Button
                onClick={handlePrintPDF}
                variant="contained"
                disabled={printLoading}
                startIcon={
                  printLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Print />
                  )
                }
                sx={{
                  bgcolor: "primary.main",
                  borderRadius: 0,
                  textTransform: "none",
                  color: "#fff",
                  "&:hover": { bgcolor: "primary.dark" },
                }}
              >
                {printLoading ? "Generating..." : "Print PDF"}
              </Button>
            </Box>
          </>
        )}
      </Dialog>

      {/* ── DELETE POPOVER ── */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={handleCloseDelete}
        // ✅ ADD - ye scroll lock disable karega
        disableScrollLock={true}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            borderRadius: 0,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            backgroundImage: "none",
            boxShadow: isDark
              ? "0 4px 16px rgba(0,0,0,0.5)"
              : "0 4px 16px rgba(0,0,0,0.12)",
            p: 1.5,
            minWidth: 180,
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "0.78rem",
            color: "text.primary",
            mb: 1,
            textAlign: "center",
          }}
        >
          Delete{" "}
          <strong style={{ color: "inherit" }}>{deleteBom?.bom_no}</strong>?
        </Typography>

        <Box sx={{ display: "flex", gap: 0.8, justifyContent: "center" }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleCloseDelete}
            disabled={deleting}
            sx={{
              borderRadius: 0,
              textTransform: "none",
              fontSize: "0.7rem",
              px: 1.5,
              py: 0.3,
              color: "text.secondary",
              borderColor: "divider",
              minWidth: 60,
              "&:hover": {
                borderColor: "primary.main",
                color: "primary.main",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
            sx={{
              borderRadius: 0,
              textTransform: "none",
              fontSize: "0.7rem",
              px: 1.5,
              py: 0.3,
              bgcolor: "error.main",
              color: "#fff",
              minWidth: 60,
              "&:hover": { bgcolor: "error.dark" },
            }}
          >
            {deleting ? (
              <CircularProgress size={12} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default BOMList;
